import { useEffect, useState } from "react";
import DemoShowcase from "../demo/DemoShowcase";
import { trackEvent } from "../../lib/analytics";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

const FORMAT_LABELS = {
  fixed_kits: "Fixed kits",
  mix_match: "Mix and match",
  byob: "Build-your-own bundle",
  upsells: "Upsell layer",
};

const STATUS_CHIPS = {
  missing: { label: "Missing", icon: "✕", classes: "bg-blush text-ink" },
  partial: { label: "Partial", icon: "~", classes: "bg-sand text-ink" },
  present: { label: "Live", icon: "✓", classes: "bg-mint text-ink" },
};

/* The BFCM angle on each scored format, shown only in the readiness skin.
   Static on purpose: the tips layer is our own playbook, not per-store AI. */
const BFCM_TIPS = {
  fixed_kits: "Gift sets are the easiest BFCM yes: one price, one add to cart, zero thinking for the shopper.",
  mix_match: "Mix and match turns one discount into a bigger basket: shoppers add more to hit the deal.",
  byob: "Build-your-own boxes are the gifting format: one builder covers everyone on a shopper's list.",
  upsells: "Peak traffic with no upsell layer means your biggest audience of the year buys exactly one thing.",
};

/* The free tips layer on the readiness page: our bundle playbook for Q4,
   same for every store, worth sharing on its own. */
const BFCM_PLAYBOOK = [
  { title: "Launch by early October", body: "Bundles need live sales data to tune contents and pricing. Launch early and your BFCM ads point at offers already proven." },
  { title: "Anchor sets to your bestseller", body: "Your highest-traffic product page is your best bundle billboard. Build the first gift set around the product people already come for." },
  { title: "Ladder your price points", body: "Gift shoppers arrive with a budget in mind. A good, better, best spread gives $25, $50 and $100 each somewhere to land." },
  { title: "Show the savings", body: "A compare-at price on the bundle does the discount math for the shopper. Visible savings is what makes a set read as a deal." },
  { title: "Sync inventory to components", body: "Bundles that oversell components become the January refund pile. Component-level tracking is why bundle sellers see 80% fewer fulfillment issues." },
  { title: "Give bundles a home", body: "A dedicated gift or bundles collection gives every ad, email and influencer link one obvious place to land." },
];

const money = (currency, value) => `${currency}${Number(value ?? 0).toFixed(2)}`;

/* "$4.95 to $6.20", collapsing to a single figure when the ends meet. */
const moneyRange = (currency, low, high) =>
  Number(low) === Number(high) ? money(currency, low) : `${money(currency, low)} to ${money(currency, high)}`;

/* The audit report: score and pain for free (score, gap, missing formats),
   then straight to the six bundles behind the email gate. The locked half is
   never in the DOM until the server hands it over at unlock. When the teaser
   carries a `bfcm` payload (the readiness door), the same page wears its Q4
   skin: countdown, readiness band, BFCM tips and the playbook. */
export default function AovAuditReport({ teaser, report: initialReport, claimUrl, supportUrl }) {
  const [report, setReport] = useState(initialReport);
  const unlocked = Boolean(report);
  const bfcm = teaser.bfcm;

  return (
    <div className="space-y-16">
      <ScoreHero teaser={teaser} bfcm={bfcm} />
      {bfcm && <BfcmClock bfcm={bfcm} />}
      <GapBars gap={teaser.gap} currency={teaser.currencySymbol} bfcm={bfcm} />
      <FormatChecklist teaser={teaser} report={report} bfcm={bfcm} />
      {bfcm && <BfcmPlaybook />}

      {unlocked ? (
        <UnlockedReport teaser={teaser} report={report} claimUrl={claimUrl} supportUrl={supportUrl} bfcm={bfcm} />
      ) : (
        <UnlockGate teaser={teaser} onUnlocked={setReport} bfcm={bfcm} />
      )}
    </div>
  );
}

/* ---------- Above the gate ---------- */

function ScoreHero({ teaser, bfcm }) {
  return (
    <section className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
      <div>
        <p className="kicker mb-4" data-reveal>
          {bfcm ? `BFCM readiness score for ${teaser.storeName}` : `AOV audit for ${teaser.storeName}`} · {teaser.domain}
        </p>
        <h1 className="display max-w-2xl text-4xl sm:text-5xl" data-reveal data-reveal-delay="60">{teaser.headline}</h1>
        <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft" data-reveal data-reveal-delay="140">{teaser.subheadline}</p>
        <div className="mt-7 flex flex-wrap gap-3 text-sm" data-reveal data-reveal-delay="200">
          <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">{Number(teaser.productsCount || 0).toLocaleString()} products read</span>
          {teaser.collectionsCount > 0 && (
            <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">{teaser.collectionsCount} collections</span>
          )}
          <span className="rounded-full border border-ink/10 bg-white px-4 py-2 font-medium">
            {moneyRange(teaser.currencySymbol, teaser.gap?.gap_low, teaser.gap?.gap_high)} uncaptured per order
          </span>
        </div>
      </div>
      <div className="card p-8 text-center" data-reveal data-reveal-delay="120">
        <ScoreGauge score={teaser.score} grade={teaser.grade} />
        <p className="mt-3 font-semibold">{bfcm ? "BFCM readiness score" : "Merchandising score"}</p>
        {bfcm?.band && (
          <p className="mt-1.5 inline-block rounded-full bg-sky-wash px-3.5 py-1 text-sm font-bold text-sky-deep">{bfcm.band}</p>
        )}
        <p className="mt-1 text-sm text-ink-soft">Scored on bundle formats, upsells, merchandising depth and pricing, all from your public catalog.</p>
      </div>
    </section>
  );
}

function ScoreGauge({ score, grade }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setProgress(score || 0), 300);
    return () => clearTimeout(t);
  }, [score]);

  const r = 62;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(progress, 100) / 100);

  return (
    <div className="relative mx-auto h-44 w-44" role="img" aria-label={`Merchandising score ${score} out of 100, grade ${grade}`}>
      <svg viewBox="0 0 160 160" className="h-full w-full">
        <circle cx="80" cy="80" r={r} fill="none" stroke="var(--color-sky-wash)" strokeWidth="12" />
        <circle
          cx="80" cy="80" r={r} fill="none"
          stroke="var(--color-sky-deep)" strokeWidth="12" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={offset}
          transform="rotate(-90 80 80)"
          style={{ transition: "stroke-dashoffset 1.3s cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="display text-5xl">{grade}</span>
        <span className="mt-1 text-sm text-ink-soft">{score} / 100</span>
      </div>
    </div>
  );
}

/* The Q4 clock: how much runway is left and why it matters. Dark on purpose,
   the one urgent beat on the page. */
function BfcmClock({ bfcm }) {
  const runway = bfcm.daysLeft > 0
    ? `${bfcm.weeksLeft >= 2 ? `${bfcm.weeksLeft} weeks` : `${bfcm.daysLeft} days`} until Black Friday.`
    : "Black Friday is here.";
  return (
    <section className="rounded-[1.25rem] bg-ink p-8 text-white shadow-lift sm:p-10">
      <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
        <div>
          <p className="kicker mb-3 !text-sky">The Q4 clock</p>
          <h2 className="display text-3xl sm:text-4xl">{runway}</h2>
          <p className="mt-3 max-w-2xl leading-relaxed text-white/70">
            Bundles need a few weeks of live sales data to tune contents and pricing.
            Launch now and November 27 meets offers that are already proven, not experiments.
          </p>
        </div>
        {bfcm.daysLeft > 0 && (
          <div className="shrink-0 rounded-2xl bg-white/10 px-7 py-5 text-center">
            <div className="display text-5xl">{bfcm.daysLeft}</div>
            <div className="mt-1 text-sm text-white/70">days left</div>
          </div>
        )}
      </div>
    </section>
  );
}

function GapBars({ gap, currency, bfcm }) {
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGrown(true), 350);
    return () => clearTimeout(t);
  }, []);
  if (!gap) return null;

  const max = gap.bundled_high || 1;
  const rows = [
    { label: "A typical order today", low: gap.order_value_low, high: gap.order_value_high, color: "#4a5158" },
    { label: "The same order with bundles", low: gap.bundled_low, high: gap.bundled_high, color: "var(--color-sky-deep)" },
  ];

  return (
    <section className="card p-8 sm:p-10" data-reveal>
      <p className="kicker mb-3">The gap, per order</p>
      <h2 className="display max-w-xl text-3xl">Every {bfcm ? "BFCM order" : "order"} is {gap.lift_percent}% smaller than it could be.</h2>
      <div className="mt-8 space-y-5">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="mb-1.5 flex items-baseline justify-between gap-4 text-sm">
              <span className="font-semibold">{row.label}</span>
              <span className="font-display text-lg font-bold">{moneyRange(currency, row.low, row.high)}</span>
            </div>
            <div className="h-3.5 overflow-hidden rounded-md bg-ink/5">
              <div
                className="h-full rounded-md"
                style={{
                  width: grown ? `${(row.high / max) * 100}%` : "0%",
                  background: row.color,
                  transition: "width 1.1s cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              />
            </div>
          </div>
        ))}
      </div>
      <Methodology gap={gap} currency={currency} />
    </section>
  );
}

/* Answers "where do these numbers come from?" in plain sight, before the
   gate. Every claim on the page traces to one of these four sources. */
function Methodology({ gap, currency }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-ink/10 bg-paper p-5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left text-sm font-semibold"
        aria-expanded={open}
      >
        Where these numbers come from
        <span aria-hidden="true" className={`transition-transform ${open ? "rotate-180" : ""}`}>⌄</span>
      </button>
      {open && (
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-ink-soft">
          <li>Your catalog: read straight from your public storefront (products and collections). No API keys, no app access, no private data.</li>
          <li>Your typical order value: we can't see your real orders, so we proxy it with your median to average product price, {moneyRange(currency, gap.order_value_low, gap.order_value_high)}. That's why it's shown as a range, not a fake-precise number.</li>
          <li>The {gap.lift_percent}% lift: the published average across 25,000+ merchants using Simple Bundles. It's the only benchmark we apply, and we apply it to your own prices.</li>
          <li>The upsell check: we read the apps actually running on your storefront's homepage.</li>
        </ul>
      )}
    </div>
  );
}

function FormatChecklist({ teaser, report, bfcm }) {
  const findings = report?.formatFindings;
  const statuses = findings || teaser.formatStatuses || [];

  return (
    <section data-reveal>
      <p className="kicker mb-3">{bfcm ? "Where BFCM orders leak" : "Where the money leaks"}</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">
        {bfcm
          ? "BFCM shoppers buy in multiples. Here's your coverage."
          : "Four ways stores lift order value. Here's your coverage."}
      </h2>
      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {statuses.map((finding) => {
          const chip = STATUS_CHIPS[finding.status] || STATUS_CHIPS.missing;
          return (
            <div key={finding.key} className="card p-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="display text-xl">{FORMAT_LABELS[finding.key] || finding.key}</h3>
                <span className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${chip.classes}`}>
                  <span aria-hidden="true">{chip.icon}</span> {chip.label}
                </span>
              </div>
              {finding.insight ? (
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{finding.insight}</p>
              ) : (
                <p className="mt-3 text-sm italic text-ink-soft/70">Unlock your bundles below for the full read on this.</p>
              )}
              {bfcm && BFCM_TIPS[finding.key] && (
                <p className="mt-3 rounded-xl bg-sky-wash px-4 py-3 text-sm leading-relaxed text-sky-deep">
                  <span className="font-bold">BFCM angle: </span>{BFCM_TIPS[finding.key]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* Free value above the gate: the playbook every store gets, no email needed.
   The personalized half (their six bundles) stays behind the unlock. */
function BfcmPlaybook() {
  return (
    <section data-reveal>
      <p className="kicker mb-3">The BFCM bundle playbook</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">Six moves the ready stores make.</h2>
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {BFCM_PLAYBOOK.map((tip, index) => (
          <div key={tip.title} className="card p-7">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-sky-wash font-display text-lg font-bold text-sky-deep">{index + 1}</div>
            <h3 className="display text-xl">{tip.title}</h3>
            <p className="mt-2.5 text-[0.95rem] leading-relaxed text-ink-soft">{tip.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- The gate ---------- */

function UnlockGate({ teaser, onUnlocked, bfcm }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(teaser.unlockPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      if (response.ok) {
        trackEvent("generate_lead", { form: bfcm ? "bfcm_readiness_unlock" : "aov_audit_unlock" });
        onUnlocked(data.report);
      } else {
        setError(Object.values(data.errors || {}).flat().join(" ") || "Something went wrong. Please try again.");
        setSubmitting(false);
      }
    } catch {
      setError("We couldn't reach the server. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <section className="relative" data-reveal>
      {/* A blurred stand-in for the report below the gate; the real content
          never reaches the browser until the unlock round-trip. */}
      <div className="pointer-events-none select-none space-y-6 blur-sm" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card p-8">
            <div className="shimmer-line mb-4 h-4 w-40" />
            <div className="shimmer-line mb-2.5 h-3.5 w-full" />
            <div className="shimmer-line mb-2.5 h-3.5 w-11/12" />
            <div className="shimmer-line h-3.5 w-2/3" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-paper/20 via-paper/80 to-paper" />

      <div className="absolute inset-x-0 top-10 mx-auto max-w-lg px-5">
        <div className="card p-8 shadow-float sm:p-10">
          <p className="kicker mb-3">{bfcm ? "Your BFCM bundles are ready" : "Your bundles are ready"}</p>
          <h2 className="display text-3xl">
            {bfcm
              ? `We already built ${teaser.storeName}'s BFCM lineup.`
              : `We already built the fix for ${teaser.storeName}.`}
          </h2>
          <ul className="mt-5 space-y-2.5 text-[0.95rem] text-ink-soft">
            {(bfcm
              ? [
                  "Six BFCM-ready bundles, built from your real products",
                  "Every widget is live and interactive: gift sets, mix and match, build-your-own",
                  "The full read on each gap in your readiness score",
                ]
              : [
                  "Six ready-made bundles, built from your real products",
                  "Every widget is live and interactive — kits, mix & match, build-your-own",
                  "The full read on each format your store is missing",
                ]
            ).map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-mint text-[11px] font-bold">✓</span>
                {line}
              </li>
            ))}
          </ul>
          <form onSubmit={submit} className="mt-7" noValidate>
            <label className="block">
              <span className="mb-1.5 block text-sm font-semibold">Work email</span>
              <input
                type="email"
                required
                placeholder="you@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-ink outline-none transition placeholder:text-ink/30 focus:border-sky-deep focus:ring-4 focus:ring-sky-wash"
              />
            </label>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            <button type="submit" className="btn-primary mt-4 w-full text-base" disabled={submitting}>
              {submitting ? "Unlocking…" : bfcm ? "Show me my BFCM bundles" : "Show me my bundles"}
              {!submitting && <span aria-hidden="true">→</span>}
            </button>
          </form>
          <p className="mt-4 text-center text-xs text-ink-soft">Free · This link stays unlocked for your whole team</p>
        </div>
      </div>

      {/* Reserve height for the overlaid card on small screens */}
      <div className="invisible h-[26rem] sm:h-80" aria-hidden="true" />
    </section>
  );
}

/* ---------- Behind the gate ---------- */

/* Post-unlock, the bundles ARE the report. Straight from the gate into the
   six interactive widgets, then one closing CTA — no essay in between. The
   narrator still writes the long-form findings (verdict, market basket,
   gold standard, quick wins); they're just no longer rendered here. */
function UnlockedReport({ teaser, report, claimUrl, supportUrl, bfcm }) {
  return (
    <div className="space-y-16">
      <BundlesSection bundles={report.bundles} demoPath={report.demoPath} storeName={teaser.storeName} />
      <ClosingCta closing={report.closing} storeName={teaser.storeName} claimUrl={claimUrl} supportUrl={supportUrl} bfcm={bfcm} />
    </div>
  );
}

function BundlesSection({ bundles, demoPath, storeName }) {
  if (!bundles?.length) return null;
  return (
    <section data-reveal>
      <p className="kicker mb-3">We already built the fix</p>
      <h2 className="display max-w-2xl text-3xl sm:text-4xl">Six bundles, designed from your catalog.</h2>
      <p className="mt-4 max-w-2xl text-lg text-ink-soft">
        These aren't mockups. Every kit, set and builder below was designed from {storeName}'s
        real products. Every widget is interactive. Go ahead, click things.
      </p>
      <div className="mt-12">
        <DemoShowcase bundles={bundles} />
      </div>
      {demoPath && (
        <p className="mt-10 text-center">
          <a href={demoPath} target="_blank" rel="noopener" className="btn-ghost">Open your full demo page →</a>
        </p>
      )}
    </section>
  );
}

function ClosingCta({ closing, storeName, claimUrl, supportUrl, bfcm }) {
  return (
    <section className="card overflow-hidden bg-gradient-to-br from-sky-wash via-white to-sand p-12 text-center sm:p-16" data-reveal>
      <p className="kicker mb-4">{bfcm ? "Early bird beats November" : "The fix is ready"}</p>
      <h2 className="display mx-auto max-w-2xl text-3xl sm:text-5xl">
        {bfcm
          ? `These could be selling on ${storeName} well before Black Friday.`
          : `These could be live on ${storeName} this week.`}
      </h2>
      {bfcm?.earlyBirdOffer && (
        <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft">{bfcm.earlyBirdOffer}</p>
      )}
      {closing && <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft">{closing}</p>}
      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        <a href={claimUrl} target="_blank" rel="noopener" className="btn-primary text-base">Claim my bundles</a>
        <a href={supportUrl} target="_blank" rel="noopener" className="btn-ghost text-base">Talk to our team</a>
      </div>
      <p className="mt-6 text-sm text-ink-soft">4.9★ on the Shopify App Store · Trusted by 25,000+ merchants · 20% more AOV is the typical result</p>
    </section>
  );
}
